import { useCallback } from "react";

import { useTranslations } from "lib/i18n";

interface PracticeArea {
  typeId: number;
  typeName: string;
}

/**
 * Practice area names and their help text arrive from the API in English, so
 * they cannot be localized at the source. Each one carries a `typeId`, which is
 * the database key the filter dropdown and the consultation form already submit
 * back to the server -- it cannot be renumbered without breaking those requests,
 * which makes it the one field here that is safe to map on. `typeName` is not:
 * rewording it server-side would silently drop the translation.
 *
 * Unknown ids fall through to the API's own text, so an area added server-side
 * shows up in English instead of disappearing. English deliberately has no
 * mapping at all and always renders whatever the API sent -- see
 * messages/es/lawyers.json.
 */
export function usePracticeArea() {
  const t = useTranslations("lawyers");

  const name = useCallback(
    (area: PracticeArea) =>
      t.optional(`practiceAreas.${area.typeId}.name`) ?? area.typeName,
    [t],
  );

  /**
   * Returns raw HTML: the API sends `<br>`-separated sentences and the caller
   * hands it to `dangerouslySetInnerHTML` through `purify`. Translations follow
   * the same shape, and go through the same sanitizer.
   */
  const description = useCallback(
    (area: PracticeArea & { description: string }) =>
      t.optional(`practiceAreas.${area.typeId}.description`) ??
      area.description,
    [t],
  );

  return { name, description };
}
